import { ChatIcon } from "@phosphor-icons/react/dist/csr/Chat";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { DELIVERY_PALETTE } from "@/core/colors";
import type { PayrollStatementSmsDelivery } from "./types";
import { getSmsDeliveryReason, getSmsDeliveryStatusLabel } from "./utils";

/**
 * The text that went with a PayPack email, one quiet line under the email's own
 * status. Only a failure is coloured: a skipped text (no number, replied STOP) is
 * the technician's choice or missing data, not something that went wrong.
 */
export const SmsDeliveryStatus = ({
  sms,
}: {
  sms: PayrollStatementSmsDelivery;
}) => {
  const isFailed = sms.status === "failed";
  const reason = getSmsDeliveryReason(sms);
  const label = `${getSmsDeliveryStatusLabel(sms)}${reason ? ` · ${reason}` : ""}`;

  // Capped so a long provider error truncates instead of widening the table.
  return (
    <Stack
      direction="row"
      align="center"
      gap={1}
      className="max-w-56 min-w-0"
      title={label}
    >
      <ChatIcon
        className={cn("h-3.5 w-3.5 shrink-0", {
          [DELIVERY_PALETTE.failed.foreground]: isFailed,
          [DELIVERY_PALETTE.sent.foreground]: sms.status === "succeeded",
          [DELIVERY_PALETTE.pending.foreground]:
            !isFailed && sms.status !== "succeeded",
        })}
      />
      <Text
        variant="caption"
        tone={isFailed ? "destructive" : "muted"}
        className="truncate"
      >
        {label}
      </Text>
    </Stack>
  );
};
